import { Tooltip } from "@base-ui-components/react/tooltip"
import cx from "classnames"
import { AlertTriangle, Flag, LoaderCircle, Play } from "lucide-react"
import { observer } from "mobx-react"
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"
import { savedActionIcon } from "../../actions/actionIcons"
import { ACTION_COLOR_STYLES } from "../../actions/actionStyles"
import { usePortalContainer } from "../../hooks/usePortalContainer"
import { useCodeStore } from "../../store/context"
import type {
    TaskTimelineActionEntry,
    TaskTimelineChapterEntry,
    TaskTimelineDoEntry,
    TaskTimelineEntry,
    TaskTimelineNavigationTarget,
} from "../../timeline/taskTimelineTypes"

const compactDateFormatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" })
const exactDateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" })

function entryTime(entry: TaskTimelineEntry, now: number): { relative: string; exact: string } {
    const timestamp = new Date(entry.createdAt)
    if (Number.isNaN(timestamp.getTime())) return { relative: "", exact: entry.createdAt }

    const age = Math.max(0, now - timestamp.getTime())
    const minute = 60 * 1_000
    const hour = 60 * minute
    const day = 24 * hour
    let relative = "Now"
    if (age >= 7 * day) relative = compactDateFormatter.format(timestamp)
    else if (age >= day) relative = `${Math.floor(age / day)}d`
    else if (age >= hour) relative = `${Math.floor(age / hour)}h`
    else if (age >= minute) relative = `${Math.floor(age / minute)}m`

    return { relative, exact: exactDateFormatter.format(timestamp) }
}

function TimelineMarker({
    label,
    detail,
    relativeTime,
    exactTime,
    portalContainer,
    expanded,
    onActivate,
    children,
}: {
    label: string
    detail?: string
    relativeTime?: string
    exactTime?: string
    portalContainer: HTMLElement | null
    expanded: boolean
    onActivate?: () => void
    children: ReactNode
}) {
    const ariaLabel = [label, detail, relativeTime, exactTime].filter(Boolean).join(". ")
    const markerVisual = <span className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center bg-base-100">{children}</span>

    if (expanded) {
        const content = (
            <>
                {markerVisual}
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-baseline gap-2">
                        <div className="min-w-0 flex-1 truncate text-xs font-medium leading-4 text-base-content">{label}</div>
                        {relativeTime && (
                            <time className="shrink-0 text-[10px] leading-4 tabular-nums text-muted/80" title={exactTime} data-task-timeline-time="true">
                                {relativeTime}
                            </time>
                        )}
                    </div>
                    {detail && <div className="truncate text-[10px] leading-4 text-muted">{detail}</div>}
                </div>
            </>
        )
        return onActivate ? (
            <button
                type="button"
                className="flex min-h-10 w-full min-w-0 cursor-pointer items-center gap-2.5 bg-transparent px-2 py-1.5 text-left outline-none transition-colors hover:bg-primary/10 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-border"
                aria-label={ariaLabel}
                data-task-timeline-marker="true"
                onClick={onActivate}
            >
                {content}
            </button>
        ) : (
            <div className="flex min-h-10 min-w-0 items-center gap-2.5 px-2 py-1.5" aria-label={ariaLabel} data-task-timeline-marker="true">
                {content}
            </div>
        )
    }

    const marker = onActivate ? (
        <button
            type="button"
            className="relative z-[1] flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center bg-base-100 outline-none transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-border"
            aria-label={ariaLabel}
            data-task-timeline-marker="true"
            onClick={onActivate}
        >
            {children}
        </button>
    ) : (
        <span
            className="relative z-[1] flex h-6 w-6 shrink-0 cursor-default items-center justify-center bg-base-100"
            aria-label={ariaLabel}
            data-task-timeline-marker="true"
        >
            {children}
        </span>
    )

    return (
        <div className="flex h-10 items-center justify-center">
            <Tooltip.Root delay={0}>
                <Tooltip.Trigger render={marker} />
                <Tooltip.Portal container={portalContainer}>
                    <Tooltip.Positioner side="left" align="center" sideOffset={8}>
                        <Tooltip.Popup className="z-50 w-52 border border-border bg-base-100 px-3 py-2 text-left shadow-lg transition-[transform,opacity] data-[ending-style]:translate-x-1 data-[ending-style]:opacity-0 data-[starting-style]:translate-x-1 data-[starting-style]:opacity-0">
                            <div className="text-xs font-medium leading-4 text-base-content">{label}</div>
                            {detail && <div className="mt-0.5 text-[10px] leading-4 text-muted">{detail}</div>}
                            {(relativeTime || exactTime) && (
                                <div className="mt-1.5 border-t border-border pt-1.5 text-[10px] leading-4 text-muted">
                                    {[relativeTime, exactTime].filter(Boolean).join(" · ")}
                                </div>
                            )}
                        </Tooltip.Popup>
                    </Tooltip.Positioner>
                </Tooltip.Portal>
            </Tooltip.Root>
        </div>
    )
}

const ChapterRow = ({
    entry,
    isCurrent,
    now,
    portalContainer,
    expanded,
    onNavigate,
}: {
    entry: TaskTimelineChapterEntry
    isCurrent: boolean
    now: number
    portalContainer: HTMLElement | null
    expanded: boolean
    onNavigate?: (target: TaskTimelineNavigationTarget) => void
}) => {
    const time = entryTime(entry, now)
    return (
        <div className="relative">
            <TimelineMarker
                label={entry.title}
                detail="Title updated"
                relativeTime={time.relative}
                exactTime={time.exact}
                portalContainer={portalContainer}
                expanded={expanded}
                onActivate={timelineActivation(entry, onNavigate)}
            >
                <span
                    className={cx(
                        "absolute inset-0 flex items-center justify-center border",
                        isCurrent ? "border-primary/50 bg-primary/10 text-primary" : "border-border text-muted"
                    )}
                >
                    <Flag className="h-3.5 w-3.5" />
                </span>
            </TimelineMarker>
        </div>
    )
}

const ActionRows = ({
    entry,
    now,
    portalContainer,
    expanded,
    onNavigate,
}: {
    entry: TaskTimelineActionEntry
    now: number
    portalContainer: HTMLElement | null
    expanded: boolean
    onNavigate?: (target: TaskTimelineNavigationTarget) => void
}) => (
    <>
        {entry.actions.map((savedAction, index) => {
            const Icon = savedActionIcon(savedAction.icon)
            const time = entryTime(entry, now)
            return (
                <div key={`${entry.id}.${index}.${savedAction.id}`} className="relative">
                    <TimelineMarker
                        label={savedAction.name}
                        detail={entry.withInstructions ? "With instructions" : undefined}
                        relativeTime={time.relative}
                        exactTime={time.exact}
                        portalContainer={portalContainer}
                        expanded={expanded}
                        onActivate={timelineActivation(entry, onNavigate)}
                    >
                        <span className={cx("absolute inset-0 flex items-center justify-center", ACTION_COLOR_STYLES[savedAction.color].swatch)}>
                            <Icon className="h-3.5 w-3.5" />
                        </span>
                    </TimelineMarker>
                </div>
            )
        })}
    </>
)

const DoRow = ({
    entry,
    now,
    portalContainer,
    expanded,
    onNavigate,
}: {
    entry: TaskTimelineDoEntry
    now: number
    portalContainer: HTMLElement | null
    expanded: boolean
    onNavigate?: (target: TaskTimelineNavigationTarget) => void
}) => {
    const time = entryTime(entry, now)
    const detail = entry.commentCount > 0 ? `${entry.commentCount} attached ${entry.commentCount === 1 ? "comment" : "comments"}` : undefined
    return (
        <div className="relative">
            <TimelineMarker
                label="Input"
                detail={detail}
                relativeTime={time.relative}
                exactTime={time.exact}
                portalContainer={portalContainer}
                expanded={expanded}
                onActivate={timelineActivation(entry, onNavigate)}
            >
                <span className="absolute inset-0 flex items-center justify-center border border-border bg-base-100 text-success/80">
                    <Play className="h-3.5 w-3.5" />
                </span>
            </TimelineMarker>
        </div>
    )
}

function timelineActivation(entry: TaskTimelineEntry, onNavigate: ((target: TaskTimelineNavigationTarget) => void) | undefined): (() => void) | undefined {
    const anchor = entry.anchor
    if (!anchor || !onNavigate) return undefined
    return () => onNavigate({ entryId: entry.id, createdAt: entry.createdAt, anchor })
}

interface TaskTimelineRailProps {
    taskId: string
    workspaceId: string
    expanded?: boolean
    onNavigate?: (target: TaskTimelineNavigationTarget) => void
}

export const TaskTimelineRail = observer(({ taskId, workspaceId, expanded = false, onNavigate }: TaskTimelineRailProps) => {
    const codeStore = useCodeStore()
    const entries = codeStore.timelines.entriesForTask(taskId)
    const error = codeStore.timelines.errorForTask(taskId)
    const isRunning = codeStore.isTaskRunning(taskId)
    const portalContainer = usePortalContainer()
    const scrollRegionRef = useRef<HTMLDivElement>(null)
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        void codeStore.timelines.loadTask(taskId, workspaceId).catch((loadError) => {
            console.warn("[TaskTimelineRail] Failed to load the selected task timeline", { taskId, loadError })
        })
    }, [codeStore, taskId, workspaceId])

    useEffect(() => {
        if (entries.length === 0) return
        const interval = window.setInterval(() => setNow(Date.now()), 60_000)
        return () => window.clearInterval(interval)
    }, [entries.length])

    useLayoutEffect(() => {
        const scrollRegion = scrollRegionRef.current
        if (!scrollRegion) return
        scrollRegion.scrollTop = scrollRegion.scrollHeight
    }, [entries.length, error, expanded, taskId])

    if (entries.length === 0 && !error) return null

    const currentChapterId = [...entries].reverse().find((entry) => entry.kind === "chapter")?.id
    const eventCount = entries.reduce((count, entry) => count + (entry.kind === "action" ? entry.actions.length : 1), 0)
    return (
        <aside
            className={cx("flex min-h-0 w-full flex-1 flex-col", expanded && "px-1")}
            aria-label={`Task timeline, ${eventCount} ${eventCount === 1 ? "event" : "events"}`}
            data-task-timeline-rail="true"
        >
            <div
                ref={scrollRegionRef}
                className="min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto overscroll-contain py-1 outline-none [scrollbar-width:none] focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-border [&::-webkit-scrollbar]:hidden"
                aria-label="Timeline entries"
                data-task-timeline-scroll="true"
                tabIndex={0}
            >
                <div className="relative">
                    {entries.length > 0 ? (
                        <div className={cx("absolute bottom-5 top-5 z-0 w-px bg-border", expanded ? "left-[1.25rem]" : "left-1/2 -translate-x-1/2")} />
                    ) : null}
                    {entries.map((entry) =>
                        entry.kind === "chapter" ? (
                            <ChapterRow
                                key={entry.id}
                                entry={entry}
                                isCurrent={entry.id === currentChapterId}
                                now={now}
                                portalContainer={portalContainer}
                                expanded={expanded}
                                onNavigate={onNavigate}
                            />
                        ) : entry.kind === "do" ? (
                            <DoRow key={entry.id} entry={entry} now={now} portalContainer={portalContainer} expanded={expanded} onNavigate={onNavigate} />
                        ) : (
                            <ActionRows key={entry.id} entry={entry} now={now} portalContainer={portalContainer} expanded={expanded} onNavigate={onNavigate} />
                        )
                    )}
                    {error ? (
                        <div className="relative">
                            <TimelineMarker label="Timeline unavailable" detail={error} portalContainer={portalContainer} expanded={expanded}>
                                <span className="absolute inset-0 flex items-center justify-center border border-warning/40 bg-warning/10 text-warning">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                </span>
                            </TimelineMarker>
                        </div>
                    ) : null}
                    <div className="relative">
                        <TimelineMarker
                            label={isRunning ? "Working" : "Current"}
                            detail={isRunning ? "Task is running" : undefined}
                            portalContainer={portalContainer}
                            expanded={expanded}
                        >
                            <span className="absolute inset-0 flex items-center justify-center border border-border bg-base-100">
                                {isRunning ? (
                                    <LoaderCircle className="h-3.5 w-3.5 animate-spin text-primary" />
                                ) : (
                                    <span className="h-2.5 w-2.5 border border-muted/50 bg-base-100" />
                                )}
                            </span>
                        </TimelineMarker>
                    </div>
                </div>
            </div>
        </aside>
    )
})
